import React from 'react';
import { useMutation } from '@apollo/client';
import I18n from '@/modules/i18n';
import SPADataTable from '@/components/admin/SysadminSPA/shared/SPADataTable';
import type { ReorderText, RowMove } from '@/components/admin/SysadminSPA/shared/SPADataTable';
import showToast from '@/components/common/Toast';
import { DESTROY_SLIDE_GROUP_MUTATION } from '@/components/admin/SlideShow/Utils/slideShowGraphQL';
import useReorderSlideGroup from '@/components/admin/SlideShow/Utils/useReorderSlideGroup';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { orderedGroupIds } from '@/components/admin/SlideShow/stores/slideGroupOrder';
import AddGroupButton from './AddGroupButton';
import slideGroupColumns from './slideGroupColumns';
import { EDITING_SLIDE_GROUP, SetCurrentAction, SlideGroup } from './utils';

type CreateSlideGroupsContentProps = {
  slideShowId: number;
  setCurrentAction: SetCurrentAction;
  setCurrentSlideGroupId: (nextSlideGroupId: number | string) => void;
};

const EMPTY_MESSAGE = 'No slide groups have been added for this slideshow. '
  + 'To create a group, click the Add Group button below.';

const rowId = (group: SlideGroup) => group.id;

const CreateSlideGroupsContent = ({
  setCurrentAction,
  setCurrentSlideGroupId,
  slideShowId,
}: CreateSlideGroupsContentProps) => {
  const slideGroups: SlideGroup[] = useSlideShowContext(state => state.slideShow.slideGroups);
  const slides = useSlideShowContext(state => state.slideShow.slides);
  const updateSlideShow = useSlideShowContext(state => state.updateSlideShow);
  const locale = useSlideShowContext(state => state.locale);
  const [destroySlideGroupMutation] = useMutation(DESTROY_SLIDE_GROUP_MUTATION);
  const { moveGroupTo } = useReorderSlideGroup();

  // Same order as the sidebar, so the row index is the position the reorder endpoint expects.
  const rows = orderedGroupIds(slides, slideGroups)
    .map(id => slideGroups.find(group => String(group.id) === id))
    .filter((group): group is SlideGroup => group !== undefined);

  const groupTitle = (group: SlideGroup) => (locale === 'es' && group.titleEs) || group.titleEn;

  // The table renders these into its own live region. Unlike the sysadmin tables this popup is
  // teacher-facing, so every string is translated and the title follows the viewer's locale.
  const reorderText = ({ kind, label, position, total }: ReorderText) => {
    switch (kind) {
      case 'handleLabel':
        return I18n.t('reorder_slide_group', { title: label });
      case 'lifted':
        return I18n.t('slide_group_lifted', { position, title: label, total });
      case 'moved':
        return I18n.t('slide_group_moved_to_position', { position, total });
      case 'dropped':
        return I18n.t('slide_group_moved', { position, title: label, total });
      case 'cancelled':
        return I18n.t('slide_group_reorder_cancelled', { position, title: label, total });
      case 'failed':
        return I18n.t('error_reordering_slide_group');
      default:
        return '';
    }
  };

  const reorder = async (_ordered: SlideGroup[], move: RowMove) => {
    const moved = await moveGroupTo(move.id, move.toIndex + 1);
    // Rejecting keeps the table from flashing a success pulse — and makes it announce the
    // rollback — over the hook's revert.
    if (!moved) throw new Error('Slide group reorder failed');
  };

  const editGroup = (group: SlideGroup) => {
    setCurrentSlideGroupId(group.id);
    setCurrentAction(EDITING_SLIDE_GROUP);
  };

  const deleteGroup = (group: SlideGroup) => {
    destroySlideGroupMutation({ variables: { slideGroupId: group.id } })
      .then((res) => {
        updateSlideShow(res.data.destroySlideGroup.slideShow);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
        showToast(I18n.t('error_deleting_slide_group'), { msgType: 'error' });
      });
  };

  const columns = slideGroupColumns({ onDelete: deleteGroup, onEdit: editGroup });

  return (
    <>
      <div className="spa-theme-light tw-font-tci">
        <SPADataTable
          columns={columns}
          data={rows}
          defaultSort={[]}
          emptyMessage={EMPTY_MESSAGE}
          reorder={{
            getRowId: rowId, getRowLabel: groupTitle, onReorder: reorder, text: reorderText,
          }}
        />
      </div>
      <div className="tw-mt-4">
        <AddGroupButton slideShowId={slideShowId} />
      </div>
    </>
  );
};

export default CreateSlideGroupsContent;
